import { useState, useEffect, useMemo } from "react";
import QuoteCarousel from "@/components/QuoteCarousel";
import { useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UserButton } from "@clerk/react";
import { useSetMyRole, getMe } from "@workspace/api-client-react";
import { Heart, LifeBuoy, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useToast } from "@/hooks/use-toast";
import { safeGetItem, safeRemoveItem } from "@/lib/safeStorage";

/**
 * Compute a lightweight, stable device fingerprint from public browser properties.
 * Uses two FNV-1a passes over the combined string to produce a 16-character hex hash
 * without requiring any library import or async operation.
 */
function computeDeviceFingerprint(): string {
  const raw = [
    navigator.userAgent,
    navigator.language,
    String(screen.width),
    String(screen.height),
    String(screen.colorDepth),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    String(navigator.hardwareConcurrency ?? 0),
    String((navigator as { deviceMemory?: number }).deviceMemory ?? 0),
  ].join("|");

  let h1 = 0x811c9dc5, h2 = 0x6b5e4329;
  for (let i = 0; i < raw.length; i++) {
    const c = raw.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ c, 0x01000193) >>> 0;
  }
  return h1.toString(16).padStart(8, "0") + h2.toString(16).padStart(8, "0");
}

export default function RoleSelection() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [selecting, setSelecting] = useState<"donor" | "debtor" | null>(null);
  const [autoApplying, setAutoApplying] = useState(false);

  // Computed once on mount; stable across re-renders.
  const deviceFingerprintHash = useMemo(() => computeDeviceFingerprint(), []);

  // Load the current user profile to detect legacy accounts (role set but
  // roleConfirmedAt null) and pre-select their existing role.
  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    staleTime: 30_000,
  });

  // True when the user already has a role but hasn't confirmed it yet — they
  // are a legacy account that needs one-click re-confirmation.
  const existingUnconfirmedRole =
    me && !me.roleConfirmedAt && me.role ? (me.role as "donor" | "debtor") : null;

  const mutation = useSetMyRole({
    mutation: {
      onSuccess: (user) => {
        qc.setQueryData(["me"], user);
        if (user.role === "donor") {
          navigate("/donor");
        } else {
          navigate("/identity");
        }
      },
      onError: () => {
        setAutoApplying(false);
        toast({
          title: "Something went wrong",
          description: "Unable to set your role. Please try again.",
          variant: "destructive",
        });
        setSelecting(null);
      },
    },
  });

  useEffect(() => {
    const raw = safeGetItem("pendingRole");
    const pending = (raw === "donor" || raw === "debtor") ? raw : null;
    if (pending) safeRemoveItem("pendingRole");
    if (pending) {
      setAutoApplying(true);
      setSelecting(pending);
      mutation.mutate({ role: pending, deviceFingerprintHash });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (autoApplying || meLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  function handleSelect(role: "donor" | "debtor") {
    setSelecting(role);
    mutation.mutate({ role, deviceFingerprintHash });
  }

  // Legacy-account confirmation view — user already has a role, just needs to confirm.
  if (existingUnconfirmedRole) {
    const isConfirming = mutation.isPending;
    const roleLabel = existingUnconfirmedRole === "donor" ? "Donor" : "Debt Relief Applicant";
    const roleIcon =
      existingUnconfirmedRole === "donor" ? (
        <Heart className="w-8 h-8 text-emerald-600" />
      ) : (
        <LifeBuoy className="w-8 h-8 text-blue-600" />
      );
    const accentColor =
      existingUnconfirmedRole === "donor" ? "emerald" : "blue";

    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted flex flex-col relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1514924013411-cbf25faa35bb?w=1600&q=80&fit=crop&auto=format"
          alt="" aria-hidden loading="lazy"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.13, filter: "grayscale(0.15)", pointerEvents: "none" }}
        />
        <header className="flex items-center justify-between px-6 py-4 border-b border-border/40">
          <Logo iconSize={32} />
          <UserButton />
        </header>

        <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
          <div className="max-w-md w-full">
            <div className="text-center mb-8">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-${accentColor}-50 border-2 border-${accentColor}-200 mb-4`}>
                {roleIcon}
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Confirm your role
              </h1>
              <p className="text-muted-foreground">
                Your account is already set up as a <span className="font-semibold text-foreground">{roleLabel}</span>.
                Click below to confirm and access your dashboard.
              </p>
            </div>

            <div className={`rounded-2xl border-2 border-${accentColor}-200 bg-${accentColor}-50/40 p-6 text-center mb-4`}>
              <div className="flex items-center justify-center gap-2 mb-2">
                <ShieldCheck className={`w-5 h-5 text-${accentColor}-600`} />
                <span className={`font-semibold text-${accentColor}-800`}>{roleLabel}</span>
              </div>
              <p className={`text-sm text-${accentColor}-700`}>
                {existingUnconfirmedRole === "donor"
                  ? "You'll have access to pools, donation history, tax receipts, and your impact dashboard."
                  : "You'll have access to debt applications, verification status, and your relief dashboard."}
              </p>
            </div>

            <button
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-colors bg-${accentColor}-600 hover:bg-${accentColor}-700 text-white disabled:opacity-60`}
              disabled={isConfirming}
              onClick={() => handleSelect(existingUnconfirmedRole)}
            >
              {isConfirming ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Continue as {roleLabel} <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <p className="mt-4 text-xs text-muted-foreground text-center">
              By continuing, you agree to our{" "}
              <a href="/terms" className="underline hover:text-foreground">Terms of Service</a>{" "}
              and{" "}
              <a href="/privacy" className="underline hover:text-foreground">Privacy Policy</a>.
            </p>
          </div>
        </div>

        <QuoteCarousel />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted flex flex-col relative overflow-hidden">
      {/* Modern/abstract — bridge silhouette, ghost texture at 5% */}
      <img
        src="https://images.unsplash.com/photo-1514924013411-cbf25faa35bb?w=1600&q=80&fit=crop&auto=format"
        alt="" aria-hidden loading="lazy"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.13, filter: "grayscale(0.15)", pointerEvents: "none" }}
      />
      <header className="flex items-center justify-between px-6 py-4 border-b border-border/40">
        <Logo iconSize={32} />
        <UserButton />
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full text-center mb-10">
          <h1 className="text-3xl font-bold text-foreground mb-3">
            How are you joining DebtBridge?
          </h1>
          <p className="text-muted-foreground text-lg">
            Choose your role — this cannot be changed later. Each role gives you access to a separate, private dashboard.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-2xl">
          <RoleCard
            role="donor"
            icon={<Heart className="w-10 h-10 text-emerald-600" />}
            title="I want to Donate"
            subtitle="Help others become debt-free"
            features={[
              "Choose from 100+ debt relief pools",
              "Track your real-world impact",
              "Receive IRS tax deduction receipts",
              "Earn donor tier badges",
            ]}
            accentClass="border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50/40"
            buttonClass="bg-emerald-600 hover:bg-emerald-700 text-white"
            loading={selecting === "donor"}
            disabled={mutation.isPending}
            onSelect={() => handleSelect("donor")}
          />

          <RoleCard
            role="debtor"
            icon={<LifeBuoy className="w-10 h-10 text-blue-600" />}
            title="I need Debt Relief"
            subtitle="Submit your debt for community funding"
            features={[
              "Submit verified debt documents",
              "Get matched to a relief pool",
              "Track your application status",
              "Payment sent directly to creditor",
            ]}
            accentClass="border-blue-300 hover:border-blue-500 hover:bg-blue-50/40"
            buttonClass="bg-blue-600 hover:bg-blue-700 text-white"
            loading={selecting === "debtor"}
            disabled={mutation.isPending}
            onSelect={() => handleSelect("debtor")}
          />
        </div>

        <p className="mt-8 text-xs text-muted-foreground text-center max-w-sm">
          By continuing, you agree to our{" "}
          <a href="/terms" className="underline hover:text-foreground">Terms of Service</a>{" "}
          and{" "}
          <a href="/privacy" className="underline hover:text-foreground">Privacy Policy</a>.
        </p>
      </div>

      <QuoteCarousel />
    </div>
  );
}

interface RoleCardProps {
  role: "donor" | "debtor";
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  features: string[];
  accentClass: string;
  buttonClass: string;
  loading: boolean;
  disabled: boolean;
  onSelect: () => void;
}

function RoleCard({
  icon,
  title,
  subtitle,
  features,
  accentClass,
  buttonClass,
  loading,
  disabled,
  onSelect,
}: RoleCardProps) {
  return (
    <div
      className={`relative bg-card border-2 rounded-2xl p-8 flex flex-col gap-5 transition-all duration-200 cursor-pointer ${accentClass} ${disabled && !loading ? "opacity-60 cursor-not-allowed" : ""}`}
      onClick={!disabled ? onSelect : undefined}
    >
      <div className="flex items-center gap-4">
        <div className="p-2 rounded-xl bg-background shadow-sm">{icon}</div>
        <div>
          <h2 className="text-xl font-bold text-foreground">{title}</h2>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
      </div>

      <ul className="flex flex-col gap-2">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-foreground">
            <span className="mt-0.5 text-emerald-500 font-bold">✓</span>
            {f}
          </li>
        ))}
      </ul>

      <button
        className={`mt-auto flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm transition-colors ${buttonClass} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) onSelect();
        }}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            {title} <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>
    </div>
  );
}
